import { supabase } from '../lib/supabaseClient';
import { safeSupabaseQuery } from '../lib/supabaseUtils';
import { getCurrentTenantId } from '@/utils/tenantUtils';
import { notificationService } from './notificationService';
import { logTaskActivity } from '@/utils/activityLogger';

export interface Task {
  id: string;
  title: string;
  description?: string;
  status: 'todo' | 'pending' | 'in_progress' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  assignee_id?: string;
  user_id?: string; // Add this for backward compatibility
  creator_id: string;
  project_id?: string;
  due_date?: string;
  completed_at?: string; // Add this for task completion tracking
  created_at: string;
  updated_at: string;
  tenant_id: string;
}

export interface CreateTaskInput {
  title: string;
  description?: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  assignee_id?: string;
  project_id?: string;
  due_date?: string;
}

export interface UpdateTaskInput {
  title?: string;
  description?: string;
  status?: 'todo' | 'pending' | 'in_progress' | 'completed' | 'cancelled';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  assignee_id?: string;
  due_date?: string;
}

class TaskService {

  async getTasks(): Promise<Task[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Get current tenant ID
    const tenantId = await getCurrentTenantId();

    const result = await safeSupabaseQuery(
      async () => {
        return await supabase
          .from('tasks')
          .select('*')
          .eq('tenant_id', tenantId)
          .or(`user_id.eq.${user.id},assignee_id.eq.${user.id}`)
          .order('created_at', { ascending: false });
      },
      `tasks-all-${user.id}-${tenantId}`,
      { cacheExpiry: 30000 } // 30 seconds cache
    );

    if (result?.error) throw result.error;
    return result?.data || [];
  }

  async getAssignedToMeTasks(): Promise<Task[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const tenantId = await getCurrentTenantId();

    const result = await safeSupabaseQuery(
      async () => {
        return await supabase
          .from('tasks')
          .select('*')
          .eq('assignee_id', user.id)
          .eq('tenant_id', tenantId)
          .order('created_at', { ascending: false });
      },
      `tasks-assigned-${user.id}-${tenantId}`,
      { cacheExpiry: 30000 } // 30 seconds cache
    );

    if (result?.error) throw result.error;
    return result?.data || [];
  }

  async getCreatedByMeTasks(): Promise<Task[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const tenantId = await getCurrentTenantId();

    const result = await safeSupabaseQuery(
      async () => {
        return await supabase
          .from('tasks')
          .select('*')
          .eq('user_id', user.id)
          .eq('tenant_id', tenantId)
          .order('created_at', { ascending: false });
      },
      `tasks-created-${user.id}-${tenantId}`,
      { cacheExpiry: 30000 } // 30 seconds cache
    );

    if (result?.error) throw result.error;
    return result?.data || [];
  }

  async getTaskById(id: string): Promise<Task | null> {
    const tenantId = await getCurrentTenantId();

    const result = await safeSupabaseQuery(
      async () => {
        return await supabase
          .from('tasks')
          .select('*')
          .eq('id', id)
          .eq('tenant_id', tenantId)
          .single();
      },
      `task-${id}-${tenantId}`,
      { cacheExpiry: 60000 } // 1 minute cache
    );

    if (result?.error) throw result.error;
    return result?.data || null;
  }

  async createTask(taskData: CreateTaskInput): Promise<Task> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const tenantId = await getCurrentTenantId();

    // Fetch current user's name from database for notifications
    let senderFullName = 'System';
    try {
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('first_name, last_name')
        .eq('id', user.id)
        .single();

      if (!userError && userData) {
        senderFullName = `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || 'System';
      }
    } catch (err) {
      console.error('[taskService] Error fetching user name for notifications:', err);
    }

    const { data, error } = await supabase
      .from('tasks')
      .insert([{
        ...taskData,
        user_id: user.id,
        tenant_id: tenantId,
        status: 'todo',
        priority: taskData.priority || 'medium'
      }])
      .select()
      .single();

    if (error) throw error;

    // Log task creation activity
    try {
      await logTaskActivity.created(data.id, data.project_id, data.assignee_id, {
        title: data.title,
        priority: data.priority,
        due_date: data.due_date,
        creator_id: user.id
      }, tenantId);
    } catch (activityError) {
      console.error('Error logging task creation activity:', activityError);
    }

    // Log task assignment activity if assigned to someone else
    if (data.assignee_id && data.assignee_id !== user.id) {
      try {
        await logTaskActivity.assigned(data.id, data.assignee_id, data.project_id, {
          title: data.title,
          priority: data.priority,
          due_date: data.due_date,
          assigned_by: user.id
        }, tenantId);
      } catch (activityError) {
        console.error('Error logging task assignment activity:', activityError);
      }
    }

    // Send notification to assignee if assigned
    if (data.assignee_id && data.assignee_id !== user.id) {
      try {
        // Smart navigation: if task has project_id, link to project modal, otherwise link to task details
        let link = `/app/tasks/${data.id}`;
        if (data.project_id) {
          // Fetch project to determine if it's commercial or residential
          try {
            const { data: project } = await supabase
              .from('projects')
              .select('project_type, pipeline_id')
              .eq('id', data.project_id)
              .single();
            
            if (project && project.pipeline_id) {
              // Determine pipeline route based on project type or pipeline_id
              const isResidential = project.project_type === 'Residential' || 
                                  (project.pipeline_id && project.pipeline_id > 1); // Assuming pipeline_id 1 is commercial
              const pipelineRoute = isResidential ? 'pipeline-resi' : 'pipeline';
              link = `/app/${pipelineRoute}?pipelineId=${project.pipeline_id}&projectId=${data.project_id}&modal=true`;
            }
          } catch (error) {
            console.error('Error fetching project type for notification link:', error);
            // Fallback to task details if we can't determine project type
          }
        }
          
        await notificationService.createNotification({
          user_id: data.assignee_id,
          type: 'task_assigned',
          title: `New Task: ${data.title}`,
          message: `You have been assigned a new task: ${data.title}`,
          project_id: data.project_id, // Top-level for edge function
          metadata: {
            task_id: data.id,
            task_name: data.title, // Changed from task_title for email template compatibility
            task_title: data.title, // Keep for backward compatibility
            sender_name: senderFullName,
            task_description: data.description || '',
            priority: data.priority,
            due_date: data.due_date,
            project_id: data.project_id // Also keep in metadata for backward compatibility
          },
          sender_id: user.id,
          sender_name: senderFullName,
          link: link
        });
      } catch (notificationError) {
        console.error('Error sending task assignment notification:', notificationError);
      }
    }

    return data;
  }

  async updateTask(id: string, updates: UpdateTaskInput): Promise<Task> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const tenantId = await getCurrentTenantId();

    // Fetch current user's name from database for notifications
    let senderFullName = 'System';
    try {
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('first_name, last_name')
        .eq('id', user.id)
        .single();

      if (!userError && userData) {
        senderFullName = `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || 'System';
      }
    } catch (err) {
      console.error('[taskService] Error fetching user name for notifications:', err);
    }

    // Get current task to check for status changes
    const currentTask = await this.getTaskById(id);
    if (!currentTask) throw new Error('Task not found');

    const { data, error } = await supabase
      .from('tasks')
      .update(updates)
      .eq('id', id)
      .eq('tenant_id', tenantId)
      .select()
      .single();

    if (error) throw error;

    // Log task update activity
    try {
      await logTaskActivity.updated(data.id, data.project_id, {
        title: data.title,
        updates,
        updated_by: user.id
      }, tenantId);
    } catch (activityError) {
      console.error('Error logging task update activity:', activityError);
    }

    // Log status change activity
    if (updates.status && updates.status !== currentTask.status) {
      try {
        await logTaskActivity.statusChanged(data.id, currentTask.status, updates.status, data.project_id, {
          title: data.title,
          changed_by: user.id
        }, tenantId);
      } catch (activityError) {
        console.error('Error logging task status change activity:', activityError);
      }
    }

    // Log assignment change activity
    if (updates.assignee_id && updates.assignee_id !== currentTask.assignee_id) {
      try {
        if (currentTask.assignee_id) {
          // Task was reassigned
          await logTaskActivity.reassigned(data.id, currentTask.assignee_id, updates.assignee_id, data.project_id, {
            title: data.title,
            reassigned_by: user.id
          }, tenantId);
        } else {
          // Task was assigned for the first time
          await logTaskActivity.assigned(data.id, updates.assignee_id, data.project_id, {
            title: data.title,
            assigned_by: user.id
          }, tenantId);
        }
      } catch (activityError) {
        console.error('Error logging task assignment change activity:', activityError);
      }
    }

    // Send notifications for status changes
    if (updates.status && updates.status !== currentTask.status) {
      try {
        // Smart navigation: if task has project_id, link to project modal, otherwise link to task details
        let link = `/app/tasks/${data.id}`;
        if (data.project_id) {
          // Fetch project to determine if it's commercial or residential
          try {
            const { data: project } = await supabase
              .from('projects')
              .select('project_type, pipeline_id')
              .eq('id', data.project_id)
              .single();
            
            if (project && project.pipeline_id) {
              // Determine pipeline route based on project type or pipeline_id
              const isResidential = project.project_type === 'Residential' || 
                                  (project.pipeline_id && project.pipeline_id > 1); // Assuming pipeline_id 1 is commercial
              const pipelineRoute = isResidential ? 'pipeline-resi' : 'pipeline';
              link = `/app/${pipelineRoute}?pipelineId=${project.pipeline_id}&projectId=${data.project_id}&modal=true`;
            }
          } catch (error) {
            console.error('Error fetching project type for notification link:', error);
            // Fallback to task details if we can't determine project type
          }
        }
          
        await notificationService.createNotification({
          user_id: currentTask.user_id || currentTask.creator_id,
          type: 'task_status_changed',
          title: `Task Status Updated: ${data.title}`,
          message: `Task "${data.title}" status changed from ${currentTask.status} to ${updates.status}`,
          metadata: {
            task_id: data.id,
            task_title: data.title,
            old_status: currentTask.status,
            new_status: updates.status,
            project_id: data.project_id
          },
          sender_id: user.id,
          sender_name: 'System',
          link: link
        });
      } catch (notificationError) {
        console.error('Error sending task status notification:', notificationError);
      }
    }

    // Send notification for assignment changes
    if (updates.assignee_id && updates.assignee_id !== currentTask.assignee_id) {
      try {
        // Smart navigation: if task has project_id, link to project modal, otherwise link to task details
        let link = `/app/tasks/${data.id}`;
        if (data.project_id) {
          // Fetch project to determine if it's commercial or residential
          try {
            const { data: project } = await supabase
              .from('projects')
              .select('project_type, pipeline_id')
              .eq('id', data.project_id)
              .single();
            
            if (project && project.pipeline_id) {
              // Determine pipeline route based on project type or pipeline_id
              const isResidential = project.project_type === 'Residential' || 
                                  (project.pipeline_id && project.pipeline_id > 1); // Assuming pipeline_id 1 is commercial
              const pipelineRoute = isResidential ? 'pipeline-resi' : 'pipeline';
              link = `/app/${pipelineRoute}?pipelineId=${project.pipeline_id}&projectId=${data.project_id}&modal=true`;
            }
          } catch (error) {
            console.error('Error fetching project type for notification link:', error);
            // Fallback to task details if we can't determine project type
          }
        }
          
        await notificationService.createNotification({
          user_id: updates.assignee_id,
          type: 'task_assigned',
          title: `Task Assigned: ${data.title}`,
          message: `You have been assigned the task: ${data.title}`,
          project_id: data.project_id, // Top-level for edge function
          metadata: {
            task_id: data.id,
            task_name: data.title, // Changed from task_title for email template compatibility
            task_title: data.title, // Keep for backward compatibility
            sender_name: senderFullName,
            task_description: data.description || '',
            priority: data.priority,
            due_date: data.due_date,
            project_id: data.project_id // Also keep in metadata for backward compatibility
          },
          sender_id: user.id,
          sender_name: senderFullName,
          link: link
        });
      } catch (notificationError) {
        console.error('Error sending task assignment notification:', notificationError);
      }
    }

    return data;
  }

  async deleteTask(id: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const tenantId = await getCurrentTenantId();

    // Get task details before deletion for logging
    const taskToDelete = await this.getTaskById(id);
    
    const { error } = await supabase
      .from('tasks')
      .delete()
      .eq('id', id)
      .eq('tenant_id', tenantId);

    if (error) throw error;

    // Log task deletion activity
    if (taskToDelete) {
      try {
        await logTaskActivity.deleted(id, taskToDelete.project_id, {
          title: taskToDelete.title,
          deleted_by: user.id,
          assignee_id: taskToDelete.assignee_id
        }, tenantId);
      } catch (activityError) {
        console.error('Error logging task deletion activity:', activityError);
      }
    }
  }

  async getTasksByProject(projectId: string): Promise<Task[]> {
    const tenantId = await getCurrentTenantId();

    const result = await safeSupabaseQuery(
      async () => {
        return await supabase
          .from('tasks')
          .select('*')
          .eq('project_id', projectId)
          .eq('tenant_id', tenantId)
          .order('created_at', { ascending: false });
      },
      `tasks-project-${projectId}-${tenantId}`,
      { cacheExpiry: 30000 } // 30 seconds cache
    );

    if (result?.error) throw result.error;
    return result?.data || [];
  }

  async getTasksByStatus(status: Task['status']): Promise<Task[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const tenantId = await getCurrentTenantId();

    const result = await safeSupabaseQuery(
      async () => {
        return await supabase
          .from('tasks')
          .select('*')
          .eq('status', status)
          .eq('tenant_id', tenantId)
          .or(`user_id.eq.${user.id},assignee_id.eq.${user.id}`)
          .order('created_at', { ascending: false });
      },
      `tasks-status-${status}-${user.id}-${tenantId}`,
      { cacheExpiry: 30000 } // 30 seconds cache
    );

    if (result?.error) throw result.error;
    return result?.data || [];
  }

  async completeTask(id: string): Promise<Task> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const tenantId = await getCurrentTenantId();

    // Fetch current user's name from database for notifications
    let senderFullName = 'System';
    try {
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('first_name, last_name')
        .eq('id', user.id)
        .single();

      if (!userError && userData) {
        senderFullName = `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || 'System';
      }
    } catch (err) {
      console.error('[taskService] Error fetching user name for notifications:', err);
    }

    const { data, error } = await supabase
      .from('tasks')
      .update({
        status: 'completed',
        completed_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .eq('tenant_id', tenantId)
      .select('*')
      .single();

    if (error) throw error;

    console.log('[taskService] Task completed, data returned:', {
      id: data.id,
      title: data.title,
      project_id: data.project_id,
      hasProjectId: !!data.project_id
    });

    // Log task completion activity
    try {
      await logTaskActivity.completed(data.id, data.project_id, {
        title: data.title,
        completed_by: user.id,
        completed_at: data.completed_at
      }, tenantId);
    } catch (activityError) {
      console.error('Error logging task completion activity:', activityError);
    }

    // Send notification to task creator
    try {
      // Smart navigation: if task has project_id, link to project modal, otherwise link to task details
      const link = data.project_id
        ? `/app/tasks/${data.id}`
        : `/app/tasks/${data.id}`;

      console.log('[taskService] Creating notification with metadata:', {
        task_id: data.id,
        project_id: data.project_id,
        hasProjectId: !!data.project_id
      });

      await notificationService.createNotification({
        user_id: data.user_id || data.creator_id,
        type: 'task_completed',
        title: `Task Completed: ${data.title}`,
        message: `Task "${data.title}" has been marked as completed`,
        project_id: data.project_id, // Top-level for edge function
        metadata: {
          task_id: data.id,
          task_name: data.title, // Changed from task_title for email template compatibility
          task_title: data.title, // Keep for backward compatibility
          sender_name: senderFullName,
          completion_date: data.completed_at || new Date().toISOString(),
          completed_by: user.id,
          project_id: data.project_id // Also keep in metadata for backward compatibility
        },
        sender_id: user.id,
        sender_name: senderFullName,
        link: link
      });
    } catch (notificationError) {
      console.error('Error sending task completion notification:', notificationError);
    }

    return data;
  }

  async uncompleteTask(id: string): Promise<Task> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const tenantId = await getCurrentTenantId();

    // Fetch current user's name from database for notifications
    let senderFullName = 'System';
    try {
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('first_name, last_name')
        .eq('id', user.id)
        .single();

      if (!userError && userData) {
        senderFullName = `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || 'System';
      }
    } catch (err) {
      console.error('[taskService] Error fetching user name for notifications:', err);
    }

    const { data, error } = await supabase
      .from('tasks')
      .update({
        status: 'todo',
        completed_at: null,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .eq('tenant_id', tenantId)
      .select('*')
      .single();

    if (error) throw error;

    console.log('[taskService] Task reopened, data returned:', {
      id: data.id,
      title: data.title,
      project_id: data.project_id,
      hasProjectId: !!data.project_id
    });

    // Send notification to task creator
    try {
      // Smart navigation: if task has project_id, link to project modal, otherwise link to task details
      const link = data.project_id
        ? `/app/tasks/${data.id}`
        : `/app/tasks/${data.id}`;

      console.log('[taskService] Creating reopen notification with metadata:', {
        task_id: data.id,
        project_id: data.project_id,
        hasProjectId: !!data.project_id
      });

      await notificationService.createNotification({
        user_id: data.user_id || data.creator_id,
        type: 'task_reopened',
        title: `Task Reopened: ${data.title}`,
        message: `Task "${data.title}" has been reopened`,
        project_id: data.project_id, // Top-level for edge function
        metadata: {
          task_id: data.id,
          task_name: data.title, // For email template compatibility
          task_title: data.title, // Keep for backward compatibility
          sender_name: senderFullName,
          reopened_by: user.id,
          reopened_date: new Date().toISOString(),
          project_id: data.project_id // Also keep in metadata for backward compatibility
        },
        sender_id: user.id,
        sender_name: senderFullName,
        link: link
      });
    } catch (notificationError) {
      console.error('Error sending task reopen notification:', notificationError);
    }

    return data;
  }

  async getAssignedByMeTasks(): Promise<Task[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const tenantId = await getCurrentTenantId();

    const result = await safeSupabaseQuery(
      async () => {
        return await supabase
          .from('tasks')
          .select('*')
          .eq('user_id', user.id)
          .eq('tenant_id', tenantId)
          .not('assignee_id', 'is', null)
          .order('created_at', { ascending: false });
      },
      `tasks-assigned-by-me-${user.id}-${tenantId}`,
      { cacheExpiry: 30000 } // 30 seconds cache
    );

    if (result?.error) throw result.error;
    return result?.data || [];
  }

  async getProjectTasks(): Promise<Task[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const tenantId = await getCurrentTenantId();

    const result = await safeSupabaseQuery(
      async () => {
        return await supabase
          .from('tasks')
          .select('*')
          .eq('tenant_id', tenantId)
          .not('project_id', 'is', null)
          .order('created_at', { ascending: false });
      },
      `tasks-project-all-${user.id}-${tenantId}`,
      { cacheExpiry: 30000 } // 30 seconds cache
    );

    if (result?.error) throw result.error;
    return result?.data || [];
  }
}

export const taskService = new TaskService(); 